import { createFileRoute } from '@tanstack/react-router';
import { warm, warmBackground } from '@/api/loaders';
import { reportingQuery, reportingQueryAllPages } from '@/api/resources';
import {
  parsePerfHaloReportSearch,
  serializePerfHaloReportSearch,
  updatePerfHaloReportSearch,
} from '@/lib/perf-halo-report-search';
import type { PerfHaloReportSearchPatch } from '@/lib/perf-halo-report-search';
import {
  buildPerfHaloAnalysisRequest,
  buildPerfHaloReportFilters,
  buildPerfHaloReportRequest,
  isPerfHaloAnalysisTab,
  PERF_HALO_TAB_GROUPS,
} from '@/lib/perf-halo-report-request';
import { requireCapability } from '@/lib/route-guards';
import { PerfHaloReport } from '@/screens/perf-halo-report';

export const Route = createFileRoute('/reports/perf-halo')({
  beforeLoad: requireCapability('reports_view'),
  validateSearch: parsePerfHaloReportSearch,
  loaderDeps: ({ search }) => search,
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  loader: ({ context, deps }) => {
    const filters = buildPerfHaloReportFilters(deps);
    const unfilteredOptions = buildPerfHaloReportFilters({
      ...deps,
      productionId: undefined,
      campaignId: undefined,
      target: undefined,
      channel: undefined,
      audienceId: undefined,
      audienceRevision: undefined,
      audienceType: undefined,
      creativeId: undefined,
    });
    const trend = buildPerfHaloReportRequest(deps, ['day'], filters, 500, false);
    const active = isPerfHaloAnalysisTab(deps.tab)
      ? buildPerfHaloAnalysisRequest(deps, filters)
      : buildPerfHaloReportRequest(deps, PERF_HALO_TAB_GROUPS[deps.tab], filters);

    warmBackground(
      context.queryClient,
      reportingQueryAllPages(buildPerfHaloReportRequest(
        deps,
        ['campaign'],
        unfilteredOptions,
        500,
        false,
      )),
      reportingQueryAllPages(buildPerfHaloReportRequest(
        deps,
        ['assigned_production'],
        unfilteredOptions,
        500,
        false,
      )),
      reportingQueryAllPages(buildPerfHaloReportRequest(
        deps,
        ['audience'],
        unfilteredOptions,
        500,
        false,
      )),
      reportingQueryAllPages(buildPerfHaloReportRequest(
        deps,
        ['creative'],
        unfilteredOptions,
        500,
        false,
      )),
      ...(deps.tab === 'overview'
        ? [reportingQuery(buildPerfHaloReportRequest(
          deps,
          ['channel'],
          filters,
          500,
          false,
        ))]
        : []),
    );

    return warm(
      context.queryClient,
      reportingQuery(trend),
      reportingQueryAllPages(active),
      ...(isPerfHaloAnalysisTab(deps.tab)
        ? [reportingQuery(buildPerfHaloReportRequest(
          deps,
          ['audience_type'],
          filters,
          500,
          false,
        ))]
        : []),
    );
  },
  component: Page,
});

function Page() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <PerfHaloReport
      search={search}
      onSearchChange={(
        patch: PerfHaloReportSearchPatch,
        options?: { replace?: boolean },
      ) => {
        void navigate({
          search: (current) => serializePerfHaloReportSearch(
            updatePerfHaloReportSearch(current, patch),
          ),
          replace: options?.replace,
        });
      }}
    />
  );
}
